import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Alert,
    Snackbar,
    Tooltip,
    Card,
    CardContent,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    PowerOff as ToggleIcon,
    Visibility as ViewIcon,
    School as SchoolIcon,
    Category as CategoryIcon,
    ExpandMore as ExpandMoreIcon,
    Delete as DeleteIcon,
    Business as FacultyIcon,
    Domain as DepartmentIcon,
    Layers as LevelIcon
} from '@mui/icons-material';
import { ProgrammeAPI } from '../utilities/ProgrammeAPI';

const ProgrammeManagement = () => {
    const [programmes, setProgrammes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedProgramme, setSelectedProgramme] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // Form data
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });

    // Structure builder state
    const [programmeStructure, setProgrammeStructure] = useState({
        faculties: []
    });
    
    // Dialog states for adding items
    const [facultyDialog, setFacultyDialog] = useState(false);
    const [departmentDialog, setDepartmentDialog] = useState(false);
    const [levelDialog, setLevelDialog] = useState(false);
    const [programmeDialog, setProgrammeDialog] = useState(false);
    
    // Selection states
    const [selectedFaculty, setSelectedFaculty] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedLevel, setSelectedLevel] = useState(null);
    
    // Form states for new items
    const [newFaculty, setNewFaculty] = useState({ name: '', description: '' });
    const [newDepartment, setNewDepartment] = useState({ name: '', description: '' });
    const [newLevel, setNewLevel] = useState({ name: '', description: '' });
    const [newProgramme, setNewProgramme] = useState({ name: '', duration: '', requirements: '' });    useEffect(() => {
        fetchProgrammes();
    }, []);

    // Initialize structure when editing
    useEffect(() => {
        if (editMode && selectedProgramme) {
            try {
                const structure = JSON.parse(selectedProgramme.programmeStructureJson || '{"faculties":[]}');
                setProgrammeStructure(structure);
            } catch (error) {
                console.error('Error parsing programme structure:', error);
                setProgrammeStructure({ faculties: [] });
            }
        } else if (!editMode) {
            setProgrammeStructure({ faculties: [] });
        }
    }, [editMode, selectedProgramme]);

    // Structure management functions
    const addFaculty = () => {
        if (!newFaculty.name.trim()) return;
        
        const faculty = {
            id: `faculty-${Date.now()}`,
            name: newFaculty.name,
            description: newFaculty.description,
            departments: []
        };
        
        setProgrammeStructure(prev => ({
            ...prev,
            faculties: [...prev.faculties, faculty]
        }));
        
        setNewFaculty({ name: '', description: '' });
        setFacultyDialog(false);
    };

    const addDepartment = () => {
        if (!newDepartment.name.trim() || !selectedFaculty) return;
        
        const department = {
            id: `dept-${Date.now()}`,
            name: newDepartment.name,
            description: newDepartment.description,
            levels: []
        };
        
        setProgrammeStructure(prev => ({
            ...prev,
            faculties: prev.faculties.map(faculty =>
                faculty.id === selectedFaculty.id
                    ? { ...faculty, departments: [...faculty.departments, department] }
                    : faculty
            )
        }));
        
        setNewDepartment({ name: '', description: '' });
        setDepartmentDialog(false);
    };

    const addLevel = () => {
        if (!newLevel.name.trim() || !selectedDepartment) return;
        
        const level = {
            id: `level-${Date.now()}`,
            name: newLevel.name,
            description: newLevel.description,
            programmes: []
        };
        
        setProgrammeStructure(prev => ({
            ...prev,
            faculties: prev.faculties.map(faculty =>
                faculty.id === selectedFaculty.id
                    ? {
                        ...faculty,
                        departments: faculty.departments.map(dept =>
                            dept.id === selectedDepartment.id
                                ? { ...dept, levels: [...dept.levels, level] }
                                : dept
                        )
                    }
                    : faculty
            )
        }));
        
        setNewLevel({ name: '', description: '' });
        setLevelDialog(false);
    };

    const addProgramme = () => {
        if (!newProgramme.name.trim() || !selectedLevel) return;
        
        const programme = {
            id: `prog-${Date.now()}`,
            name: newProgramme.name,
            duration: newProgramme.duration,
            requirements: newProgramme.requirements
        };
        
        setProgrammeStructure(prev => ({
            ...prev,
            faculties: prev.faculties.map(faculty =>
                faculty.id === selectedFaculty.id
                    ? {
                        ...faculty,
                        departments: faculty.departments.map(dept =>
                            dept.id === selectedDepartment.id
                                ? {
                                    ...dept,
                                    levels: dept.levels.map(level =>
                                        level.id === selectedLevel.id
                                            ? { ...level, programmes: [...level.programmes, programme] }
                                            : level
                                    )
                                }
                                : dept
                        )
                    }
                    : faculty
            )
        }));
        
        setNewProgramme({ name: '', duration: '', requirements: '' });
        setProgrammeDialog(false);
    };

    const removeFaculty = (facultyId) => {
        setProgrammeStructure(prev => ({
            ...prev,
            faculties: prev.faculties.filter(faculty => faculty.id !== facultyId)
        }));
    };

    const removeDepartment = (facultyId, departmentId) => {
        setProgrammeStructure(prev => ({
            ...prev,
            faculties: prev.faculties.map(faculty =>
                faculty.id === facultyId
                    ? { ...faculty, departments: faculty.departments.filter(dept => dept.id !== departmentId) }
                    : faculty
            )
        }));
    };

    const removeLevel = (facultyId, departmentId, levelId) => {
        setProgrammeStructure(prev => ({
            ...prev,
            faculties: prev.faculties.map(faculty =>
                faculty.id === facultyId
                    ? {
                        ...faculty,
                        departments: faculty.departments.map(dept =>
                            dept.id === departmentId
                                ? { ...dept, levels: dept.levels.filter(level => level.id !== levelId) }
                                : dept
                        )
                    }
                    : faculty
            )
        }));
    };

    const removeProgramme = (facultyId, departmentId, levelId, programmeId) => {
        setProgrammeStructure(prev => ({
            ...prev,
            faculties: prev.faculties.map(faculty =>
                faculty.id === facultyId
                    ? {
                        ...faculty,
                        departments: faculty.departments.map(dept =>
                            dept.id === departmentId
                                ? {
                                    ...dept,
                                    levels: dept.levels.map(level =>
                                        level.id === levelId
                                            ? { ...level, programmes: level.programmes.filter(prog => prog.id !== programmeId) }
                                            : level
                                    )
                                }
                                : dept
                        )
                    }
                    : faculty
            )
        }));
    };    const fetchProgrammes = async () => {
        try {
            setLoading(true);
            const response = await ProgrammeAPI.getAllProgrammes();
            setProgrammes(response.data || []);
        } catch (error) {
            console.error('Error fetching programmes:', error);
            showSnackbar(`Error connecting to server: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };    const handleSubmit = async () => {
        try {
            const programmeData = {
                name: formData.name,
                description: formData.description,
                programmeStructureJson: JSON.stringify(programmeStructure)
            };

            let response;
            if (editMode) {
                response = await ProgrammeAPI.updateProgramme(selectedProgramme.id, programmeData, 'current-user');
            } else {
                response = await ProgrammeAPI.createProgramme(programmeData, 'current-user');
            }
            
            showSnackbar(editMode ? 'Programme updated successfully' : 'Programme created successfully');
            handleClose();
            fetchProgrammes();
        } catch (error) {
            console.error('Error saving programme:', error);
            showSnackbar(`Error saving programme: ${error.message}`, 'error');
        }
    };const handleEdit = (programme) => {
        setSelectedProgramme(programme);
        setFormData({
            name: programme.name,
            description: programme.description || ''
        });
        setEditMode(true);
        setOpen(true);
    };    const handleToggleStatus = async (programmeId) => {
        if (!window.confirm('Are you sure you want to toggle this programme status?')) {
            return;
        }

        try {
            const response = await ProgrammeAPI.toggleProgrammeStatus(programmeId, 'current-user');
            showSnackbar('Programme status updated successfully');
            fetchProgrammes();
        } catch (error) {
            console.error('Error toggling programme status:', error);
            showSnackbar(`Error updating programme status: ${error.message}`, 'error');
        }
    };const handleClose = () => {
        setOpen(false);
        setEditMode(false);
        setSelectedProgramme(null);
        setFormData({ name: '', description: '' });
        setProgrammeStructure({ faculties: [] });
        setSelectedFaculty(null);
        setSelectedDepartment(null);
        setSelectedLevel(null);
    };    const handleAddNew = () => {
        setFormData({
            name: '',
            description: ''
        });
        setEditMode(false);
        setOpen(true);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const parseStructurePreview = (jsonString) => {
        try {
            const structure = JSON.parse(jsonString);
            const facultyCount = structure.faculties?.length || 0;
            const deptCount = structure.faculties?.reduce((acc, faculty) => 
                acc + (faculty.departments?.length || 0), 0) || 0;
            const progCount = structure.faculties?.reduce((acc, faculty) => 
                acc + (faculty.departments?.reduce((deptAcc, dept) => 
                    deptAcc + (dept.levels?.reduce((levelAcc, level) => 
                        levelAcc + (level.programmes?.length || 0), 0) || 0), 0) || 0), 0) || 0;
            
            return `${facultyCount} Faculties, ${deptCount} Departments, ${progCount} Programmes`;
        } catch (e) {
            return 'Invalid JSON structure';
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Programme Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddNew}
                >
                    Add New Programme Structure
                </Button>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <SchoolIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                                <Box>
                                    <Typography variant="h6">{programmes.length}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Programme Structures
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CategoryIcon color="secondary" sx={{ mr: 2, fontSize: 40 }} />
                                <Box>
                                    <Typography variant="h6">
                                        {programmes.filter(p => !p.archived || p.archived === 'NO').length}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Active Structures
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Programmes Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Structure Preview</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created Date</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    Loading programmes...
                                </TableCell>
                            </TableRow>
                        ) : programmes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No programmes found. Add your first programme structure!
                                </TableCell>
                            </TableRow>
                        ) : (
                            programmes.map((programme) => (
                                <TableRow key={programme.id}>
                                    <TableCell>
                                        <Typography variant="subtitle2">
                                            {programme.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {programme.description || 'No description'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {parseStructurePreview(programme.programmeStructureJson)}
                                        </Typography>
                                    </TableCell>                                    <TableCell>
                                        <Chip
                                            label={programme.enabled === false ? 'Disabled' : 'Enabled'}
                                            color={programme.enabled === false ? 'error' : 'success'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(programme.createdAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleEdit(programme)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>                                        <Tooltip title={programme.enabled === false ? 'Enable Programme' : 'Disable Programme'}>
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleToggleStatus(programme.id)}
                                                color={programme.enabled === false ? 'success' : 'error'}
                                            >
                                                <ToggleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add/Edit Dialog */}            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {editMode ? 'Edit Programme Structure' : 'Create New Programme Structure'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        {/* Basic Information */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    autoFocus
                                    label="Structure Name"
                                    fullWidth
                                    variant="outlined"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Description"
                                    fullWidth
                                    variant="outlined"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {/* Structure Builder */}
                        <Typography variant="h6" gutterBottom>
                            Programme Structure Builder
                        </Typography>
                        
                        {/* Add Faculty Button */}
                        <Box sx={{ mb: 2 }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setFacultyDialog(true)}
                                color="primary"
                            >
                                Add Faculty
                            </Button>
                        </Box>

                        {/* Faculties List */}
                        {programmeStructure.faculties.map((faculty) => (
                            <Accordion key={faculty.id} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <FacultyIcon sx={{ mr: 1, color: 'primary.main' }} />
                                        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                            {faculty.name}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeFaculty(faculty.id);
                                            }}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ pl: 2 }}>
                                        {faculty.description && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {faculty.description}
                                            </Typography>
                                        )}
                                        
                                        {/* Add Department Button */}
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                                setSelectedFaculty(faculty);
                                                setDepartmentDialog(true);
                                            }}
                                            sx={{ mb: 2 }}
                                        >
                                            Add Department
                                        </Button>

                                        {/* Departments List */}
                                        {faculty.departments?.map((department) => (
                                            <Accordion key={department.id} sx={{ mb: 1 }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <DepartmentIcon sx={{ mr: 1, color: 'secondary.main' }} />
                                                        <Typography variant="body1" sx={{ flexGrow: 1 }}>
                                                            {department.name}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeDepartment(faculty.id, department.id);
                                                            }}
                                                            color="error"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Box sx={{ pl: 2 }}>
                                                        {department.description && (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                {department.description}
                                                            </Typography>
                                                        )}
                                                        
                                                        {/* Add Level Button */}
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<AddIcon />}
                                                            onClick={() => {
                                                                setSelectedFaculty(faculty);
                                                                setSelectedDepartment(department);
                                                                setLevelDialog(true);
                                                            }}
                                                            sx={{ mb: 2 }}
                                                        >
                                                            Add Level
                                                        </Button>

                                                        {/* Levels List */}
                                                        {department.levels?.map((level) => (
                                                            <Accordion key={level.id} sx={{ mb: 1 }}>
                                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                                        <LevelIcon sx={{ mr: 1, color: 'success.main' }} />
                                                                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                                                            {level.name}
                                                                        </Typography>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                removeLevel(faculty.id, department.id, level.id);
                                                                            }}
                                                                            color="error"
                                                                        >
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    </Box>
                                                                </AccordionSummary>
                                                                <AccordionDetails>
                                                                    <Box sx={{ pl: 2 }}>
                                                                        {level.description && (
                                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                                {level.description}
                                                                            </Typography>
                                                                        )}
                                                                        
                                                                        {/* Add Programme Button */}
                                                                        <Button
                                                                            variant="outlined"
                                                                            size="small"
                                                                            startIcon={<AddIcon />}
                                                                            onClick={() => {
                                                                                setSelectedFaculty(faculty);
                                                                                setSelectedDepartment(department);
                                                                                setSelectedLevel(level);
                                                                                setProgrammeDialog(true);
                                                                            }}
                                                                            sx={{ mb: 2 }}
                                                                        >
                                                                            Add Programme
                                                                        </Button>

                                                                        {/* Programmes List */}
                                                                        {level.programmes?.map((programme) => (
                                                                            <Card key={programme.id} variant="outlined" sx={{ mb: 1 }}>
                                                                                <CardContent sx={{ py: 1 }}>
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                                        <Box>
                                                                                            <Typography variant="body2" fontWeight="bold">
                                                                                                {programme.name}
                                                                                            </Typography>
                                                                                            {programme.duration && (
                                                                                                <Typography variant="caption" color="text.secondary">
                                                                                                    Duration: {programme.duration}
                                                                                                </Typography>
                                                                                            )}
                                                                                            {programme.requirements && (
                                                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                                                    Requirements: {programme.requirements}
                                                                                                </Typography>
                                                                                            )}
                                                                                        </Box>
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            onClick={() => removeProgramme(faculty.id, department.id, level.id, programme.id)}
                                                                                            color="error"
                                                                                        >
                                                                                            <DeleteIcon />
                                                                                        </IconButton>
                                                                                    </Box>
                                                                                </CardContent>
                                                                            </Card>
                                                                        ))}
                                                                    </Box>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        ))}
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        {programmeStructure.faculties.length === 0 && (
                            <Alert severity="info">
                                Start by adding a faculty to build your programme structure.
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained"
                        disabled={!formData.name.trim()}
                    >
                        {editMode ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Faculty Dialog */}
            <Dialog open={facultyDialog} onClose={() => setFacultyDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Faculty</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            autoFocus
                            label="Faculty Name"
                            fullWidth
                            variant="outlined"
                            value={newFaculty.name}
                            onChange={(e) => setNewFaculty({ ...newFaculty, name: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Description (Optional)"
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            value={newFaculty.description}
                            onChange={(e) => setNewFaculty({ ...newFaculty, description: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFacultyDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={addFaculty} 
                        variant="contained"
                        disabled={!newFaculty.name.trim()}
                    >
                        Add Faculty
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Department Dialog */}
            <Dialog open={departmentDialog} onClose={() => setDepartmentDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Department to {selectedFaculty?.name}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            autoFocus
                            label="Department Name"
                            fullWidth
                            variant="outlined"
                            value={newDepartment.name}
                            onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Description (Optional)"
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            value={newDepartment.description}
                            onChange={(e) => setNewDepartment({ ...newDepartment, description: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDepartmentDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={addDepartment} 
                        variant="contained"
                        disabled={!newDepartment.name.trim()}
                    >
                        Add Department
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Level Dialog */}
            <Dialog open={levelDialog} onClose={() => setLevelDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Level to {selectedDepartment?.name}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            autoFocus
                            label="Level Name"
                            fullWidth
                            variant="outlined"
                            value={newLevel.name}
                            onChange={(e) => setNewLevel({ ...newLevel, name: e.target.value })}
                            sx={{ mb: 2 }}
                            placeholder="e.g., Undergraduate, Postgraduate, Diploma"
                        />
                        <TextField
                            label="Description (Optional)"
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            value={newLevel.description}
                            onChange={(e) => setNewLevel({ ...newLevel, description: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLevelDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={addLevel} 
                        variant="contained"
                        disabled={!newLevel.name.trim()}
                    >
                        Add Level
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Programme Dialog */}
            <Dialog open={programmeDialog} onClose={() => setProgrammeDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Programme to {selectedLevel?.name}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            autoFocus
                            label="Programme Name"
                            fullWidth
                            variant="outlined"
                            value={newProgramme.name}
                            onChange={(e) => setNewProgramme({ ...newProgramme, name: e.target.value })}
                            sx={{ mb: 2 }}
                            placeholder="e.g., Bachelor of Science in Computer Science"
                        />
                        <TextField
                            label="Duration"
                            fullWidth
                            variant="outlined"
                            value={newProgramme.duration}
                            onChange={(e) => setNewProgramme({ ...newProgramme, duration: e.target.value })}
                            sx={{ mb: 2 }}
                            placeholder="e.g., 4 years, 2 years, 6 months"
                        />
                        <TextField
                            label="Entry Requirements"
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            value={newProgramme.requirements}
                            onChange={(e) => setNewProgramme({ ...newProgramme, requirements: e.target.value })}
                            placeholder="e.g., High School Certificate, Bachelor's Degree"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setProgrammeDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={addProgramme} 
                        variant="contained"
                        disabled={!newProgramme.name.trim()}
                    >
                        Add Programme
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ProgrammeManagement;
